import { subscribeToMicrophoneSamples } from '@shared/DSPHandler/services/MicrophoneService';
import { V2_RESULT_STATUS } from '../runtime/types';
import {
  calculateNoiseCompensatedRmsDb,
  RANGE_MIN_DB,
} from './audioLevel';

const MEASUREMENT_INTERVAL_MS = 100;
const MINIMUM_MEASUREMENT_COUNT = 3;

const summarizeMeasurements = (measurements, targetLowDbfs, targetHighDbfs) => {
  if (!measurements.length) {
    return {
      measurements: [],
      averageDbfs: null,
      minimumDbfs: null,
      maximumDbfs: null,
      timeInTargetPercent: 0,
    };
  }

  const values = measurements.map(({ valueDbfs }) => valueDbfs);
  const inTargetCount = values.filter(
    (value) => value >= targetLowDbfs && value <= targetHighDbfs,
  ).length;

  return {
    measurements,
    averageDbfs: values.reduce((sum, value) => sum + value, 0) / values.length,
    minimumDbfs: Math.min(...values),
    maximumDbfs: Math.max(...values),
    timeInTargetPercent: (inTargetCount / values.length) * 100,
  };
};

export const createVoiceLoudnessController = (config) => {
  let effectiveConfig = config;
  let collecting = false;
  let repetitionStartedAt = null;
  let measurements = [];
  let lastMeasurementAt = 0;
  let pendingSamples = [];

  const unsubscribe = subscribeToMicrophoneSamples((samples) => {
    if (!collecting) return;
    for (let index = 0; index < samples.length; index += 1) {
      pendingSamples.push(samples[index]);
    }

    const now = Date.now();
    if (now - lastMeasurementAt < MEASUREMENT_INTERVAL_MS) return;
    lastMeasurementAt = now;

    const valueDbfs = calculateNoiseCompensatedRmsDb(pendingSamples);
    pendingSamples = [];
    if (valueDbfs === RANGE_MIN_DB) return;
    measurements.push({
      offsetMs: now - repetitionStartedAt,
      valueDbfs,
    });
  });

  return {
    startProduction: () => {},
    startRepetition: () => {
      repetitionStartedAt = Date.now();
      lastMeasurementAt = 0;
      measurements = [];
      pendingSamples = [];
      collecting = true;
    },
    finishRepetition: () => {
      collecting = false;
      if (measurements.length < MINIMUM_MEASUREMENT_COUNT) {
        collecting = true;
        return { status: V2_RESULT_STATUS.INCOMPLETE };
      }
      return {
        status: V2_RESULT_STATUS.COMPLETED,
        result: summarizeMeasurements(
          measurements,
          effectiveConfig.targetLowDbfs,
          effectiveConfig.targetHighDbfs,
        ),
      };
    },
    finishProduction: ({ repetitions }) => {
      const allMeasurements = repetitions.flatMap(
        (repetition) => repetition.result?.measurements || [],
      );
      return summarizeMeasurements(
        allMeasurements,
        effectiveConfig.targetLowDbfs,
        effectiveConfig.targetHighDbfs,
      );
    },
    pause: () => {
      collecting = false;
      pendingSamples = [];
    },
    resume: () => {
      lastMeasurementAt = 0;
      collecting = true;
    },
    updateConfig: (nextConfig) => {
      effectiveConfig = nextConfig;
    },
    cancel: () => {
      collecting = false;
      unsubscribe();
    },
    restore: () => {},
  };
};